/* =========================================================================
   PG Design Studio · In-cart "Change sizes" popup
   - Triggered by `[data-pg-ds-edit-sizes]` buttons in cart drawer & cart page
   - Fetches /cart.js (to find all lines for this design) and
     /products/<source-handle>.js (to enumerate available sizes)
   - Renders a color × size qty grid styled like the widget's OrderStep
   - Save → /cart/update.js for qty changes/removals + /cart/add.js for new
     size combos, then refreshes the cart drawer or reloads the cart page
   ========================================================================= */
(function () {
  if (window.__pgDsCartEditInit) return;
  window.__pgDsCartEditInit = true;

  // Threshold for the quote-only handoff. Mirrors widget/lib/pricing.ts.
  var QUOTE_THRESHOLD = 1000;
  var NO_SIZE = "*"; // sentinel for sizeless products (matches widget/lib/store.ts)
  // Admin API base — same as widget/lib/cart.ts DEFAULT_API_BASE_URL.
  var ADMIN_API_BASE = "https://admin-iota-ebon.vercel.app";

  // ── DOM helpers ─────────────────────────────────────────────────────────
  function h(tag, attrs, children) {
    var el = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        var v = attrs[k];
        // Skip nullish — `setAttribute("disabled", null)` would otherwise
        // set disabled="null" and permanently disable the element.
        if (v == null || v === false) return;
        if (k === "style" && typeof v === "object") {
          Object.assign(el.style, v);
        } else if (k === "dataset") {
          Object.assign(el.dataset, v);
        } else if (k.indexOf("on") === 0 && typeof v === "function") {
          el.addEventListener(k.slice(2).toLowerCase(), v);
        } else if (k === "className") {
          el.className = v;
        } else if (v === true) {
          // Boolean true → present attribute with empty string value
          el.setAttribute(k, "");
        } else {
          el.setAttribute(k, v);
        }
      });
    }
    (children || []).forEach(function (c) {
      if (c == null || c === false) return;
      if (typeof c === "string" || typeof c === "number") {
        el.appendChild(document.createTextNode(String(c)));
      } else {
        el.appendChild(c);
      }
    });
    return el;
  }

  /** Canonical color key — trims + collapses whitespace. Casing preserved
   *  to match Shopify's option value (case-sensitive). */
  function canon(c) {
    return (c == null ? "" : String(c)).trim().replace(/\s+/g, " ");
  }

  function colorToHex(name) {
    var map = {
      black: "#0a0a0a", white: "#fafafa", navy: "#1d2a4d", red: "#c22b2b",
      royal: "#1f4ec2", gold: "#d6b04a", yellow: "#f1d04a", maroon: "#7a1f2b",
      gray: "#7c7c7c", grey: "#7c7c7c", "cool grey": "#8a8c91",
      green: "#2e7d4a", "forest green": "#214d31", "kelly green": "#1f9d4a",
      orange: "#e67a2c", pink: "#e36aa6", "hot pink": "#e0367e",
      purple: "#5b3a8c", brown: "#6a4a2e", tan: "#c9b692", sand: "#dfd3b3",
      cream: "#efe7d3", charcoal: "#34373b", silver: "#bcbcbe",
      "true royal": "#1f4ec2", "true navy": "#1d2a4d",
      "athletic heather": "#bcc0c6", heather: "#b9bcc1",
    };
    var k = (name || "").trim().toLowerCase();
    return map[k] || null;
  }

  // ── State ───────────────────────────────────────────────────────────────
  var modalRoot = null;
  // state: { designId, sourceHandle, productTitle, methodLabel,
  //          colors, sizes, isSizeless,
  //          variantIdByColor, currentByColorSize, // qty before edit
  //          editedGrid, // qty after edit
  //          siblingPropsByColor, // props to copy onto new /cart/add.js lines
  //          lineKeyByColorSize, // cart line index (1-based) per existing line
  //          isCartPage }
  var state = null;

  // ── Modal mount ─────────────────────────────────────────────────────────
  function ensureRoot() {
    if (modalRoot) return modalRoot;
    modalRoot = document.createElement("div");
    modalRoot.className = "pg-ds-edit-modal-root";
    document.body.appendChild(modalRoot);
    return modalRoot;
  }

  function close() {
    if (modalRoot) modalRoot.innerHTML = "";
    document.body.style.overflow = "";
  }

  function showLoading() {
    var root = ensureRoot();
    root.innerHTML = "";
    var overlay = h("div", { className: "pg-ds-edit-modal-overlay" }, [
      h("div", { className: "pg-ds-edit-modal" }, [
        h("div", { className: "pg-ds-edit-loading" }, ["Loading your design…"]),
      ]),
    ]);
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) close();
    });
    root.appendChild(overlay);
    document.body.style.overflow = "hidden";
  }

  function showError(msg) {
    var root = ensureRoot();
    root.innerHTML = "";
    var overlay = h("div", { className: "pg-ds-edit-modal-overlay" }, [
      h("div", { className: "pg-ds-edit-modal" }, [
        h("div", { className: "pg-ds-edit-modal-header" }, [
          h("button", { className: "pg-ds-edit-modal-close", "aria-label": "Close", onclick: close }, ["×"]),
          h("div", { className: "pg-ds-edit-modal-kicker" }, ["Something went wrong"]),
          h("h2", { className: "pg-ds-edit-modal-title" }, ["Can't edit this right now."]),
          h("p", { className: "pg-ds-edit-modal-sub" }, [msg || "Please try again, or go to the design page to make changes."]),
        ]),
      ]),
    ]);
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) close();
    });
    root.appendChild(overlay);
  }

  // ── Open ────────────────────────────────────────────────────────────────
  async function openModal(designId, sourceHandle) {
    showLoading();
    try {
      var cartRes = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      var cart = await cartRes.json();
      var prodRes = await fetch("/products/" + encodeURIComponent(sourceHandle) + ".js", { headers: { Accept: "application/json" } });
      if (!prodRes.ok) throw new Error("Couldn't load source product.");
      var product = await prodRes.json();

      // All cart items with their 1-based line index.
      var allItems = (cart.items || []).map(function (it, i) {
        var item = Object.assign({}, it);
        item._lineIndex = i + 1;
        return item;
      });
      var designItems = allItems.filter(function (it) {
        return it.properties && it.properties._ds_design_id === designId;
      });

      if (designItems.length === 0) {
        showError("This design is no longer in your cart.");
        return;
      }

      // Inspect source product options & variants. Older Shopify .js shape
      // has options as string[], newer has [{name, values}].
      var sourceOpts = (product.options || []);
      function findOptIndex(re) {
        if (sourceOpts.length === 0) return -1;
        if (typeof sourceOpts[0] === "string") {
          return sourceOpts.findIndex(function (n) { return re.test(n); });
        }
        return sourceOpts.findIndex(function (o) { return re.test(o.name); });
      }
      function valuesForOpt(idx) {
        if (idx < 0) return [];
        if (sourceOpts[idx] && Array.isArray(sourceOpts[idx].values)) {
          return sourceOpts[idx].values.slice();
        }
        // Derive from variants: variant.option1 / option2 / option3
        var set = [];
        (product.variants || []).forEach(function (v) {
          var val = v["option" + (idx + 1)];
          if (val && set.indexOf(val) === -1) set.push(val);
        });
        return set;
      }
      var sizeIdx = findOptIndex(/^size$/i);
      var colorIdx = findOptIndex(/^colou?r$/i);
      var sizeValues = valuesForOpt(sizeIdx);
      var sourceColorValues = valuesForOpt(colorIdx);
      var isSizeless = sizeValues.length === 0;
      if (isSizeless) sizeValues = [NO_SIZE];

      // Map source color → first variant featured_image (used as the
      // "garment image" / swatch + as the variant image we attach when
      // the customer adds a brand-new color in the popup).
      // Also build availability lookup: source color → size → bool, so we
      // can gray out sizes the supplier is sold out of.
      var sourceGarmentImageByColor = {};
      var availableByColorSize = {};
      var anyAvailableByColor = {};
      (product.variants || []).forEach(function (v) {
        var c = colorIdx >= 0 ? v["option" + (colorIdx + 1)] : null;
        if (!c) return;
        var k = canon(c);
        if (!sourceGarmentImageByColor[k]) {
          if (v.featured_image && v.featured_image.src) {
            sourceGarmentImageByColor[k] = v.featured_image.src;
          } else if (product.featured_image) {
            sourceGarmentImageByColor[k] = product.featured_image;
          }
        }
        var s = sizeIdx >= 0 ? v["option" + (sizeIdx + 1)] : NO_SIZE;
        if (!s) s = NO_SIZE;
        if (!availableByColorSize[k]) availableByColorSize[k] = {};
        // Variants without `available` (legacy shape) default to true.
        var isAvail = v.available !== false;
        availableByColorSize[k][s] = isAvail;
        if (isAvail) anyAvailableByColor[k] = true;
      });

      // Custom product ID (for add-color-variants endpoint). All design
      // lines share the same product_id (= the custom product we minted).
      var customProductId = designItems[0].product_id;

      // Colors that are in the cart for this design (we use the cart-line
      // variant_title from the custom product, which is the color name).
      var cartColors = [];
      var variantIdByColor = {};
      var currentByColorSize = {}; // color -> size -> qty
      var lineKeyByColorSize = {}; // color -> size -> 1-based line index
      var siblingPropsByColor = {}; // color -> { ..._ds_* + display props }
      // The first sibling's props are universal — keep for ANY new color/line.
      var anySiblingProps = null;

      designItems.forEach(function (it) {
        var color = it.variant_title || "Default";
        // Tidy: strip the auto-default that Shopify uses when no options are set
        if (color === "Default Title") color = "Default";
        var canonC = canon(color);
        if (cartColors.indexOf(canonC) === -1) {
          cartColors.push(canonC);
          variantIdByColor[canonC] = it.variant_id;
          currentByColorSize[canonC] = {};
          lineKeyByColorSize[canonC] = {};
        }
        var size = (it.properties && it.properties.Size) || NO_SIZE;
        currentByColorSize[canonC][size] = (currentByColorSize[canonC][size] || 0) + it.quantity;
        // Last line wins on duplicates — Shopify should consolidate but just in case
        lineKeyByColorSize[canonC][size] = it._lineIndex;

        // Capture sibling props to clone onto new /cart/add.js lines.
        // Pricing payload + product handle + design id are the SAME across
        // siblings; we only need one snapshot.
        if (!siblingPropsByColor[canonC]) {
          var clone = {};
          Object.keys(it.properties || {}).forEach(function (k) {
            // Skip Size — we'll set it explicitly per new line.
            if (k === "Size") return;
            clone[k] = it.properties[k];
          });
          siblingPropsByColor[canonC] = clone;
          if (!anySiblingProps) anySiblingProps = clone;
        }
      });

      // Build the full color list: cart colors first (in their cart order),
      // then any source-product colors that aren't in the cart yet.
      var allColors = cartColors.slice();
      sourceColorValues.forEach(function (c) {
        var k = canon(c);
        if (k && allColors.indexOf(k) === -1) allColors.push(k);
      });
      // If the source product had no Color option (or the cart line's color
      // doesn't match any source option), fall back to cart colors only.
      if (allColors.length === 0) allColors = cartColors.slice();

      // Initialize editedGrid with current values; cells for not-yet-in-cart
      // colors default to 0.
      var editedGrid = {};
      allColors.forEach(function (c) {
        editedGrid[c] = {};
        sizeValues.forEach(function (s) {
          editedGrid[c][s] = (currentByColorSize[c] && currentByColorSize[c][s]) || 0;
        });
      });

      var methodLabel =
        (designItems[0].properties && (designItems[0].properties["Print method"] || designItems[0].properties._ds_method_label)) ||
        "Custom";

      state = {
        designId: designId,
        sourceHandle: sourceHandle,
        customProductId: customProductId,
        productTitle: product.title || "Custom design",
        methodLabel: methodLabel,
        cartColors: cartColors,          // colors that already have a variant
        allColors: allColors,            // every color row to render (cart + new)
        sizes: sizeValues,
        isSizeless: isSizeless,
        variantIdByColor: variantIdByColor,
        sourceGarmentImageByColor: sourceGarmentImageByColor,
        availableByColorSize: availableByColorSize,
        anyAvailableByColor: anyAvailableByColor,
        currentByColorSize: currentByColorSize,
        editedGrid: editedGrid,
        siblingPropsByColor: siblingPropsByColor,
        anySiblingProps: anySiblingProps || {},
        lineKeyByColorSize: lineKeyByColorSize,
        // Populated from add-color-variants response — pre-baked design-on-
        // garment composites stored as a metafield at original add-to-cart.
        mockupByColor: {},
        isCartPage: window.location.pathname === "/cart" || /\/cart\?/.test(window.location.href),
        saving: false,
        err: null,
        // Accordion: only one color row reveals its size+qty grid at a time.
        // Default to the first cart color so the customer immediately sees the
        // sizes they originally ordered (most edits are tweaks to that row).
        expandedColor: cartColors[0] || allColors[0] || null,
      };
      render();
    } catch (e) {
      console.error("[pg-ds-cart-edit] open failed:", e);
      showError(e && e.message ? e.message : null);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────
  function totalForColor(color) {
    var sum = 0;
    var sizes = state.editedGrid[color] || {};
    Object.keys(sizes).forEach(function (s) { sum += sizes[s] || 0; });
    return sum;
  }
  function totalAll() {
    var sum = 0;
    state.allColors.forEach(function (c) { sum += totalForColor(c); });
    return sum;
  }

  function toggleExpandedColor(color) {
    state.expandedColor = state.expandedColor === color ? null : color;
    renderBody();
  }

  function setQty(color, size, qty) {
    if (qty < 0) qty = 0;
    if (qty > 9999) qty = 9999;
    state.editedGrid[color][size] = qty;
    // Re-render just the inputs + totals to avoid losing focus is non-trivial,
    // so we re-render the body region only.
    renderBody();
    renderFooter();
  }

  function render() {
    var root = ensureRoot();
    root.innerHTML = "";
    var overlay = h("div", { className: "pg-ds-edit-modal-overlay", role: "dialog", "aria-modal": "true" });
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay && !state.saving) close();
    });

    var modal = h("div", { className: "pg-ds-edit-modal", id: "pgDsEditModal" });

    var header = h("div", { className: "pg-ds-edit-modal-header" }, [
      h("button", { className: "pg-ds-edit-modal-close", "aria-label": "Close", onclick: function () { if (!state.saving) close(); } }, ["×"]),
      h("div", { className: "pg-ds-edit-modal-kicker" }, [state.methodLabel + " · saved design"]),
      h("h2", { className: "pg-ds-edit-modal-title" }, ["Change sizes & quantities."]),
      h("p", { className: "pg-ds-edit-modal-sub" }, [state.productTitle]),
    ]);

    var body = h("div", { className: "pg-ds-edit-modal-body", id: "pgDsEditBody" });
    var footer = h("div", { className: "pg-ds-edit-modal-footer", id: "pgDsEditFooter" });

    modal.appendChild(header);
    modal.appendChild(body);
    modal.appendChild(footer);
    overlay.appendChild(modal);
    root.appendChild(overlay);

    renderBody();
    renderFooter();
  }

  function renderBody() {
    var body = document.getElementById("pgDsEditBody");
    if (!body) return;
    body.innerHTML = "";

    if (state.err) {
      body.appendChild(h("div", { className: "pg-ds-edit-error" }, [state.err]));
    }

    state.allColors.forEach(function (color) {
      var isInCart = state.cartColors.indexOf(color) !== -1;
      var swatchHex = colorToHex(color);
      var garmentImage = state.sourceGarmentImageByColor[color];

      // Prefer the source product's garment image as the swatch so colors
      // are visually unambiguous. Fall back to a CSS color or a generic
      // neutral when neither is available.
      var swatch;
      if (garmentImage) {
        swatch = h("span", {
          className: "pg-ds-edit-color-swatch",
          style: {
            backgroundImage: 'url(' + garmentImage + ')',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            background: '#f0f0f0 url(' + garmentImage + ') center/cover',
          },
        });
      } else {
        swatch = h("span", {
          className: "pg-ds-edit-color-swatch",
          style: swatchHex ? { background: swatchHex } : {},
        });
      }

      // Collapsed-by-default header — clicking the pill toggles the size +
      // qty grid below. Keeps the popup scannable when a cart has many
      // colors instead of dumping every size input on the customer at once.
      var rowTotal = totalForColor(color);
      var isExpanded = state.expandedColor === color;
      // A color is fully sold out only when (a) it's not yet in the cart
      // and (b) the source product has zero available variants for it.
      var colorFullyOOS = !isInCart
        && state.availableByColorSize[color]
        && !state.anyAvailableByColor[color];
      var head = h("button", {
        type: "button",
        className: "pg-ds-edit-color-row-head"
          + (isExpanded ? " is-expanded" : "")
          + (colorFullyOOS ? " is-oos" : ""),
        "aria-expanded": isExpanded ? "true" : "false",
        disabled: colorFullyOOS ? "" : null,
        onclick: function () {
          if (colorFullyOOS) return;
          toggleExpandedColor(color);
        },
      }, [
        h("div", { className: "pg-ds-edit-color-name" }, [
          swatch,
          color,
          colorFullyOOS
            ? h("span", { className: "pg-ds-edit-color-oos-tag" }, ["sold out"])
            : (isInCart ? null : h("span", { className: "pg-ds-edit-color-add-tag" }, ["add"])),
        ]),
        h("div", { className: "pg-ds-edit-color-total" }, [
          rowTotal > 0
            ? h("span", {}, ["Total: ", h("b", {}, [String(rowTotal)]), " pc", rowTotal === 1 ? "" : "s"])
            : colorFullyOOS
              ? h("span", { className: "pg-ds-edit-color-empty" }, ["Unavailable"])
              : h("span", { className: "pg-ds-edit-color-empty" }, ["Pick sizes"]),
        ]),
        (function () {
          var chev = document.createElementNS("http://www.w3.org/2000/svg", "svg");
          chev.setAttribute("class", "pg-ds-edit-chevron");
          chev.setAttribute("viewBox", "0 0 24 24");
          chev.setAttribute("width", "14");
          chev.setAttribute("height", "14");
          chev.setAttribute("fill", "none");
          chev.setAttribute("stroke", "currentColor");
          chev.setAttribute("stroke-width", "2.4");
          chev.setAttribute("stroke-linecap", "round");
          chev.setAttribute("stroke-linejoin", "round");
          chev.setAttribute("aria-hidden", "true");
          var poly = document.createElementNS("http://www.w3.org/2000/svg", "polyline");
          poly.setAttribute("points", "6 9 12 15 18 9");
          chev.appendChild(poly);
          return chev;
        })(),
      ]);

      var rowChildren = [head];
      if (isExpanded) {
        var grid = h("div", { className: "pg-ds-edit-size-grid" + (state.isSizeless ? " pg-ds-edit-sizeless" : "") });
        state.sizes.forEach(function (size) {
          var qty = state.editedGrid[color][size] || 0;
          // Source-product inventory check. Sizes the supplier is sold out
          // of can't be added — but if the customer already has some in
          // their cart, they can still decrement (size lookup missing →
          // assume available, e.g. cart-only colors not in source).
          var availMap = state.availableByColorSize[color];
          var isAvail = !availMap || availMap[size] !== false;
          var currentQty = (state.currentByColorSize[color] && state.currentByColorSize[color][size]) || 0;
          var disableAll = !isAvail && qty === 0;
          var disableIncrease = !isAvail && qty >= currentQty;
          var cellCls = "pg-ds-edit-size-cell";
          if (qty > 0) cellCls += " is-active";
          if (disableAll) cellCls += " is-oos";
          var cell = h("div", { className: cellCls }, [
            state.isSizeless ? null : h("div", { className: "pg-ds-edit-size-label" }, [size]),
            disableAll
              ? h("div", { className: "pg-ds-edit-oos-label" }, ["Sold out"])
              : h("div", { className: "pg-ds-edit-stepper" }, [
                  h("button", {
                    type: "button",
                    className: "pg-ds-edit-step",
                    "aria-label": "Decrease",
                    disabled: qty <= 0 ? "" : null,
                    onclick: function () { setQty(color, size, qty - 1); },
                  }, ["−"]),
                  (function () {
                    var input = h("input", {
                      className: "pg-ds-edit-input",
                      type: "number",
                      min: "0",
                      max: "9999",
                      value: String(qty),
                      "data-color": color,
                      "data-size": size,
                    });
                    input.addEventListener("input", function (e) {
                      var v = parseInt(e.target.value, 10);
                      if (isNaN(v) || v < 0) v = 0;
                      // Clamp typed-in increases when source is OOS.
                      if (!isAvail && v > currentQty) v = currentQty;
                      state.editedGrid[color][size] = v;
                      e.target.value = String(v);
                      renderFooter();
                      var newQty = state.editedGrid[color][size];
                      if (newQty > 0) cell.classList.add("is-active");
                      else cell.classList.remove("is-active");
                    });
                    input.addEventListener("blur", function () {
                      renderBody();
                      renderFooter();
                    });
                    return input;
                  })(),
                  h("button", {
                    type: "button",
                    className: "pg-ds-edit-step",
                    "aria-label": "Increase",
                    disabled: disableIncrease ? "" : null,
                    title: disableIncrease ? "Sold out — can't add more" : null,
                    onclick: function () {
                      if (disableIncrease) return;
                      setQty(color, size, qty + 1);
                    },
                  }, ["+"]),
                ]),
          ]);
          grid.appendChild(cell);
        });
        rowChildren.push(grid);
      }

      var row = h("div", { className: "pg-ds-edit-color-row" + (isExpanded ? " is-expanded" : "") }, rowChildren);
      body.appendChild(row);
    });

    // Footer link to design editor (canvas tweaks). Adding new colors is
    // now supported in-place via the rows above (rows tagged "ADD" are
    // source colors that aren't yet in the cart).
    body.appendChild(h("div", { className: "pg-ds-edit-extra" }, [
      h("span", {}, ["Want to tweak the design itself?"]),
      h("a", { href: "/products/" + encodeURIComponent(state.sourceHandle) + "?ds_edit=" + encodeURIComponent(state.designId) }, ["Open design studio →"]),
    ]));
  }

  function renderFooter() {
    var footer = document.getElementById("pgDsEditFooter");
    if (!footer) return;
    // Toggle the saving class on the parent modal so the body gets blurred.
    var modalEl = document.getElementById("pgDsEditModal");
    if (modalEl) {
      if (state.saving) modalEl.classList.add("is-saving");
      else modalEl.classList.remove("is-saving");
    }
    footer.innerHTML = "";
    var total = totalAll();
    var summaryChildren = [
      "Total: ",
      h("b", {}, [String(total)]),
      " pc" + (total === 1 ? "" : "s"),
    ];
    if (total >= QUOTE_THRESHOLD) {
      summaryChildren.push(h("span", { className: "pg-ds-edit-block" }, [QUOTE_THRESHOLD + "+ pcs requires a custom quote — open the design studio to request one."]));
    } else if (total === 0) {
      summaryChildren.push(h("span", { className: "pg-ds-edit-warn" }, ["This will remove the design from your cart."]));
    }
    var summary = h("div", { className: "pg-ds-edit-summary" }, summaryChildren);

    // Build the Save button. While saving: keep primary styling, show
    // spinner before the label, and disable interaction without graying
    // out (so it stays visually identifiable to the customer).
    var saveBtnChildren = [];
    if (state.saving) {
      var spinner = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      spinner.setAttribute("class", "pg-ds-edit-spinner");
      spinner.setAttribute("viewBox", "0 0 22 22");
      spinner.setAttribute("aria-hidden", "true");
      var circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("class", "pg-ds-edit-spinner-path");
      circle.setAttribute("cx", "11");
      circle.setAttribute("cy", "11");
      circle.setAttribute("r", "9");
      spinner.appendChild(circle);
      saveBtnChildren.push(spinner);
    }
    saveBtnChildren.push(state.saving ? "Saving…" : "Save changes");

    var saveBtn = h("button", {
      type: "button",
      className: "pg-ds-edit-btn pg-ds-edit-btn-primary" + (state.saving ? " pg-ds-edit-btn-loading" : ""),
      disabled: state.saving || total >= QUOTE_THRESHOLD,
      onclick: save,
    }, saveBtnChildren);

    var cancelBtn = h("button", {
      type: "button",
      className: "pg-ds-edit-btn pg-ds-edit-btn-ghost",
      disabled: state.saving,
      onclick: function () { if (!state.saving) close(); },
    }, ["Cancel"]);

    footer.appendChild(summary);
    footer.appendChild(h("div", { className: "pg-ds-edit-actions" }, [cancelBtn, saveBtn]));
  }

  // ── Save ────────────────────────────────────────────────────────────────
  async function save() {
    if (state.saving) return;
    state.saving = true;
    state.err = null;
    renderFooter();

    try {
      // ── Step 1: Mint variants for any "new" colors the customer added.
      // New = a color row that's in the source product but has no existing
      // cart line. We POST to /api/add-color-variants which creates a
      // variant on the existing custom product + attaches the source
      // garment image. Response gives us variantIds keyed by color.
      var newColorsToAdd = [];
      state.allColors.forEach(function (color) {
        if (state.cartColors.indexOf(color) !== -1) return; // already has variant
        var perSize = state.editedGrid[color] || {};
        var anyQty = Object.keys(perSize).some(function (s) { return (perSize[s] || 0) > 0; });
        if (!anyQty) return;
        newColorsToAdd.push({
          name: color,
          garmentImageUrl: state.sourceGarmentImageByColor[color] || null,
        });
      });

      if (newColorsToAdd.length > 0) {
        if (!state.customProductId) {
          throw new Error("Couldn't find the custom product ID for this design.");
        }
        var addColorsResp = await fetch(ADMIN_API_BASE + "/api/add-color-variants", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            productId: state.customProductId,
            newColors: newColorsToAdd,
          }),
        });
        if (!addColorsResp.ok) {
          var ct = await addColorsResp.text().catch(function () { return ""; });
          throw new Error("Couldn't add color variants (" + addColorsResp.status + ") " + ct.slice(0, 160));
        }
        var newVariantData = await addColorsResp.json();
        if (!newVariantData.ok || !newVariantData.variantsByColor) {
          throw new Error(newVariantData.error || "Add-color-variants returned no variants");
        }
        // Merge the new variants into state for the /cart/add.js step below.
        Object.keys(newVariantData.variantsByColor).forEach(function (color) {
          state.variantIdByColor[color] = newVariantData.variantsByColor[color];
          state.cartColors.push(color); // treat as in-cart now
        });

        // Pre-baked per-color composites returned by add-color-variants
        // (sourced from the `custom.ds_color_mockups` metafield written by
        // create-custom-product at original add-to-cart). Used below to
        // set `_ds_proof_url` on the new cart lines — instant, no
        // compositing or upload at edit time. Missing colors fall back to
        // the cloned sibling proof.
        if (newVariantData.mockupByColor && typeof newVariantData.mockupByColor === "object") {
          Object.keys(newVariantData.mockupByColor).forEach(function (color) {
            var url = newVariantData.mockupByColor[color];
            if (url) state.mockupByColor[canon(color)] = url;
          });
        }
      }

      // ── Step 2: Compute diff vs current cart.
      // Updates: every existing cart line gets a new qty (possibly same;
      // 0 removes). Iterate over lineKeyByColorSize so we touch every
      // existing line exactly once.
      var updates = {};
      state.allColors.forEach(function (color) {
        var perSize = state.lineKeyByColorSize[color] || {};
        Object.keys(perSize).forEach(function (size) {
          var newQty = (state.editedGrid[color] && state.editedGrid[color][size]) || 0;
          var oldQty = (state.currentByColorSize[color] && state.currentByColorSize[color][size]) || 0;
          if (newQty !== oldQty) updates[perSize[size]] = newQty;
        });
      });

      // Adds: cells where qty > 0 and there's no existing cart line for
      // that (color, size). Covers brand-new (color, size) combos AND
      // every (color, size) for the colors we just minted variants for.
      var adds = [];
      state.allColors.forEach(function (color) {
        var perSize = state.lineKeyByColorSize[color] || {};
        Object.keys(state.editedGrid[color]).forEach(function (size) {
          var qty = state.editedGrid[color][size] || 0;
          if (qty <= 0) return;
          if (perSize[size]) return; // existing line — covered by updates
          if (!state.variantIdByColor[color]) return; // shouldn't happen post-step-1
          // Use this color's sibling props if we have them, else any sibling.
          // Sibling props carry _ds_pricing_v2 + design id so Cart Transform
          // and Price Guard treat the new line as part of this design.
          var hasOwnSiblings = !!state.siblingPropsByColor[color];
          var props = Object.assign({}, state.siblingPropsByColor[color] || state.anySiblingProps || {});
          if (size !== NO_SIZE) props.Size = size;
          // For brand-new colors (no existing sibling line), swap in the
          // composite we built in step 1b — design over the NEW color's
          // garment photo. If that wasn't generated (CORS-tainted canvas,
          // upload failure, missing artwork URL) the cloned sibling
          // `_ds_proof_url` from the original color carries through as a
          // fallback — strictly better than the bare garment image.
          if (!hasOwnSiblings) {
            var perColorMockup = state.mockupByColor && state.mockupByColor[color];
            if (perColorMockup) props._ds_proof_url = perColorMockup;
            // else: leave the cloned anySiblingProps._ds_proof_url in place
          }
          adds.push({
            id: state.variantIdByColor[color],
            quantity: qty,
            properties: props,
          });
        });
      });

      if (Object.keys(updates).length === 0 && adds.length === 0) {
        // Nothing changed — just close.
        state.saving = false;
        close();
        return;
      }

      if (Object.keys(updates).length > 0) {
        var upResp = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates: updates }),
        });
        if (!upResp.ok) {
          var t = await upResp.text().catch(function () { return ""; });
          throw new Error("Update failed (" + upResp.status + ") " + t.slice(0, 160));
        }
      }
      if (adds.length > 0) {
        var addResp = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: adds }),
        });
        if (!addResp.ok) {
          var t2 = await addResp.text().catch(function () { return ""; });
          throw new Error("Add failed (" + addResp.status + ") " + t2.slice(0, 160));
        }
      }

      // Success — refresh and close.
      if (state.isCartPage) {
        window.location.reload();
        return;
      }
      await refreshCartDrawer();
      close();
    } catch (e) {
      console.error("[pg-ds-cart-edit] save failed:", e);
      state.err = (e && e.message) || "Couldn't save changes. Try again?";
      state.saving = false;
      renderBody();
      renderFooter();
    }
  }

  // ── Cart drawer refresh (Dawn / PG theme) ───────────────────────────────
  // The cart drawer is a `<cart-drawer>` custom element rendered by the
  // `cart-drawer` section. Fetch updated section HTML + swap innards, then
  // force the drawer to stay open with the new state visible — no second
  // tap, no navigation. Dispatches `cart:updated` so other listeners
  // (analytics, header cart bubble) can react.
  async function refreshCartDrawer() {
    try {
      var sections = ["cart-drawer", "cart-icon-bubble", "cart-live-region-text", "pg-cart-trust"];
      var url = "/?sections=" + sections.join(",");
      var resp = await fetch(url, { headers: { Accept: "application/json" } });
      var data = await resp.json();
      var parser = new DOMParser();
      sections.forEach(function (sec) {
        if (!data[sec]) return;
        var fresh = parser.parseFromString(data[sec], "text/html");
        var freshWrap = fresh.getElementById("shopify-section-" + sec);
        var liveWrap = document.getElementById("shopify-section-" + sec);
        if (freshWrap && liveWrap) {
          liveWrap.innerHTML = freshWrap.innerHTML;
        }
      });
      // Open + show the drawer with the new state. The web component's
      // `connectedCallback` re-binds on the newly-rendered innards.
      var drawer = document.querySelector("cart-drawer");
      if (drawer) {
        drawer.classList.remove("is-empty"); // recompute on next render anyway
        if (typeof drawer.open === "function") {
          drawer.open();
        } else {
          drawer.classList.add("animate", "active");
        }
      }
      // Let the rest of the theme know the cart changed (analytics,
      // floating cart count, etc.). Match Dawn's existing event name.
      // Includes the design ID so the DS cart-patcher can prioritize
      // re-pricing that group on the next tick.
      try {
        document.dispatchEvent(new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { source: "pg-ds-cart-edit", designId: state && state.designId || null },
        }));
      } catch (_e) { /* IE fallback not needed in Shopify */ }
    } catch (e) {
      console.warn("[pg-ds-cart-edit] refresh failed, reloading:", e);
      window.location.reload();
    }
  }

  // ── Boot — delegated click handler ──────────────────────────────────────
  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-pg-ds-edit-sizes]");
    if (!btn) return;
    e.preventDefault();
    var designId = btn.getAttribute("data-design-id") || "";
    var sourceHandle = btn.getAttribute("data-source-handle") || "";
    if (!designId || !sourceHandle) return;
    openModal(designId, sourceHandle);
  });

  // ESC closes
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && modalRoot && modalRoot.firstChild && state && !state.saving) {
      close();
    }
  });

  // ── bfcache refresh: dispatch the theme's existing cart:refresh ──
  // Customer adds items on a product page, navigates away, comes back —
  // bfcache restores the old page HTML (including a stale cart drawer +
  // cart-icon bubble). global.js has a `cart:refresh` listener that
  // re-fetches all cart sections via the sections API and swaps innerHTML,
  // but nothing dispatches the event. We dispatch it on every bfcache
  // restore so the drawer/bubble are always in sync with the actual cart.
  window.addEventListener("pageshow", function (event) {
    if (event && event.persisted) {
      try {
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      } catch (_e) { /* IE fallback not needed in Shopify */ }
    }
  });
})();
